/**
 * DateInput Container Component (Web)
 *
 * Simple passthrough that forwards onChange as onChangeText to the web View.
 * No formatting is needed on web because the browser's native date picker
 * provides YYYY-MM-DD values directly via `<input type="date">`.
 * @module components/DateInput
 */

import DateInputView from "./DateInputView";

/** Props for the DateInput component */
export interface DateInputContainerProps {
  /** Current value in YYYY-MM-DD format (web) */
  readonly value: string;
  /** Called when the date value changes */
  readonly onChange: (value: string) => void;
  /** Called when input loses focus */
  readonly onBlur?: () => void;
  /** Test identifier for the input */
  readonly testID?: string;
  /** Whether dark theme is active — controls the native calendar picker color scheme */
  readonly isDark?: boolean;
  /**
   * Accessible name for the control.
   *
   * The molecule renders a bare `<input type="date">` on web and a masked text
   * field on native, and neither carries a name of its own — the visible label
   * beside it is not associated with either. Without this a screen reader
   * announces an unnamed date field (SE-7316).
   */
  readonly accessibilityLabel?: string;
  /**
   * Earliest selectable date, ISO `YYYY-MM-DD`. Defaults to 1940-01-01.
   */
  readonly minDate?: string;
  /**
   * Latest selectable date, ISO `YYYY-MM-DD`. Defaults to TODAY, because the
   * two original consumers ask for dates that have already happened — a date
   * of birth and a historical filter range.
   *
   * SE-7316 made it a prop rather than a constant: a fixture is normally in
   * the FUTURE (pairings publish six to eight weeks ahead), so the default
   * would have made the primary case unreachable. Optional and defaulted so
   * both existing call sites keep today's behaviour untouched.
   */
  readonly maxDate?: string;
}

/**
 * Web Container that passes onChange directly as onChangeText to the View.
 * No digit formatting is needed since the browser date picker handles input
 * and always returns ISO YYYY-MM-DD values.
 * @param root0 - Component props
 * @param root0.value - Current date value in ISO format
 * @param root0.onChange - Called with the new date string value
 * @param root0.onBlur - Called when input loses focus
 * @param root0.testID - Test identifier
 * @param root0.isDark - Whether dark theme is active
 * @param root0.accessibilityLabel - Accessible name for the control
 * @param root0.minDate - Earliest selectable date (ISO), default 1940-01-01
 * @param root0.maxDate - Latest selectable date (ISO), default today
 */
const DateInputContainer = ({
  value,
  onChange,
  onBlur,
  testID,
  isDark,
  accessibilityLabel,
  minDate,
  maxDate,
}: DateInputContainerProps) => (
  <DateInputView
    value={value}
    onChangeText={onChange}
    onBlur={onBlur}
    testID={testID}
    isDark={isDark}
    accessibilityLabel={accessibilityLabel}
    minDate={minDate}
    maxDate={maxDate}
  />
);

export default DateInputContainer;
